import { Construction } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/cn';

/**
 * Honest placeholder for a route whose module has not been built yet.
 *
 * It names the phase that delivers the screen rather than showing an empty table or
 * mock data. A fake screen is worse than no screen: it looks finished, and mock figures
 * shown to a signed-in user are exactly what docs/PROJECT_OVERVIEW.md forbids.
 */
export function Placeholder({
  title,
  phase,
  description,
}: {
  title: string;
  phase: string;
  description?: string;
}) {
  return (
    <>
      <PageHeader title={title} />
      <div
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border border-dashed border-line-strong',
          'bg-subtle/50 px-6 py-20 text-center',
        )}
      >
        <div
          aria-hidden
          className="flex size-11 items-center justify-center rounded-full bg-surface text-fg-subtle shadow-xs ring-1 ring-line"
        >
          <Construction className="size-5" />
        </div>
        <div className="space-y-1.5">
          <p className="text-body font-medium">Not built yet</p>
          <p className="mx-auto max-w-md text-caption text-fg-muted">
            {description ?? `This module is delivered in ${phase}.`}
          </p>
          <p className="text-caption text-fg-muted">
            The navigation, permissions and layout around it are real — only the page
            content is pending.
          </p>
        </div>
      </div>
    </>
  );
}
